/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IPivotStyleProps, IPivotStyles, Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { IStackProps, IStackSlots } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { CluEntitiesList } from 'src/clu/appShell/cluBuildSchemaPage/components/CluEntitiesList';
import { CluIntentsList } from 'src/clu/appShell/cluBuildSchemaPage/components/CluIntentsList';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { Page } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';

const messages = defineMessages({
    intents: { id: 'clu.cluBuildSchemaPage.intents', defaultMessage: 'Intents' },
    entities: { id: 'clu.cluBuildSchemaPage.entities', defaultMessage: 'Entities' },
    pageTitle: { id: 'clu.cluBuildSchemaPage.pageTitle', defaultMessage: 'Schema definition' },
    conversationDescription: {
        id: 'clu.cluBuildSchemaPage.conversationDescription',
        defaultMessage: `Add intents and entities to your schema. Intents are tasks or
actions the user wants to perform. Entities are terms relevant to the user's intent
and can be extracted to help fulfill the user’s intent.`
    },
    orchestrationDescription: {
        id: 'clu.cluBuildSchemaPage.orchestrationDescription',
        defaultMessage: 'Add connected and non-connected intents to your schema. Intents are tasks or actions the user wants to perform.'
    }
});

export type CluBuildSchemaPagePivot = 'entityPivit' | 'intentPivot';

const { getClassName } = getStylist('CluBuildSchemaPage');
const pivotItemClassName = getClassName('PivotItem', { height: '100%' });
const pivotRootClassName = getClassName('PivotRoot', { display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' });

const pivotStyles = classNamesFunction<IPivotStyleProps, IPivotStyles>()({
    root: { marginBottom: 15 },
    itemContainer: { overflow: 'hidden', flex: 1 }
});
const intentsListContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { marginTop: '14px' }
});

export const CluBuildSchemaPage = observer(() => {
    const { localize } = useLocalization();
    const { rootStore, uiStore, dispatcher } = useGlobalState();
    const activePivot = uiStore.cluSchemaBuildPageActivePivot ?? 'intentPivot';

    const currentProject = rootStore.currentCluProjectStore;
    const isConversation = currentProject.projectType === 'Conversation';

    const onRenderPageContent = () => {
        if (isConversation) {
            return (
                <Pivot
                    linkSize="large"
                    styles={pivotStyles}
                    selectedKey={activePivot}
                    className={pivotRootClassName}
                    onLinkClick={item =>
                        dispatcher.dispatch('updateCluBuildSchemaPageActivePivot', {
                            activePivot: item.props.itemKey as CluBuildSchemaPagePivot
                        })
                    }>
                    <PivotItem itemKey="intentPivot" headerText={localize(messages.intents)} className={pivotItemClassName}>
                        <CluIntentsList />
                    </PivotItem>

                    <PivotItem itemKey="entityPivot" headerText={localize(messages.entities)} className={pivotItemClassName}>
                        <CluEntitiesList />
                    </PivotItem>
                </Pivot>
            );
        }
        return <CluIntentsList containerStyles={intentsListContainerStyles} />;
    };

    return (
        <Page size="container-fluid" pageName="BuildSchemaPage" title={localize(messages.pageTitle)}>
            <EditableTitle
                title={localize(messages.pageTitle)}
                subtitle={localize(isConversation ? messages.conversationDescription : messages.orchestrationDescription)}
            />
            {onRenderPageContent()}
        </Page>
    );
});
